// `suggest()`: every move at one chord, gated, graded, ranked. Split from
// index.ts so derive.ts can import it without a module cycle.

import type { Score } from '../score/types';
import { buildReharmContext, sequenceIndexOf } from './context';
import { REHARM_MOVES } from './moves';
import { scoreCandidate, type ScoreOptions } from './score';
import type { MelodyFit, ReharmCandidate, ReharmContext, ReharmMove, ReharmRung } from './types';

export interface SuggestOptions extends ScoreOptions {
  /** Highest rung to consult (default 8 — everything registered). */
  maxRung?: ReharmRung;
  /** Offer candidates whose melody fit is 'none'. Default false: the gate. */
  includeUnsafe?: boolean;
}

const FIT_RANK: Record<MelodyFit, number> = {
  'chord-tone': 0,
  tension: 1,
  'no-melody': 1,
  none: 3,
};

export function moveById(id: string): ReharmMove | undefined {
  return REHARM_MOVES.find((m) => m.id === id);
}

/**
 * Every candidate for the chord at sequence index `at`, gated and ranked:
 * melody fit first, then function kept, then fewest altered tensions the
 * melody leaves unresolved, then rung (simpler first), then the voice-leading
 * delta. Deterministic — the same context gives the same list.
 */
export function suggest(
  ctx: ReharmContext,
  at: number,
  options: SuggestOptions = {},
): ReharmCandidate[] {
  if (at < 0 || at >= ctx.sequence.length) return [];
  const maxRung = options.maxRung ?? 8;
  const out: ReharmCandidate[] = [];
  const counts = new Map<string, number>();
  for (const move of REHARM_MOVES) {
    if (move.rung > maxRung) continue;
    for (const proposal of move.candidates(ctx, at)) {
      const score = scoreCandidate(ctx, proposal, options);
      if (score.melodyFit === 'none' && !options.includeUnsafe) continue;
      const n = (counts.get(move.id) ?? 0) + 1;
      counts.set(move.id, n);
      out.push({ ...proposal, id: `${move.id}:${proposal.chordId}:${n}`, score });
    }
  }
  return out.sort(
    (a, b) =>
      FIT_RANK[a.score.melodyFit] - FIT_RANK[b.score.melodyFit] ||
      Number(b.score.functionKept) - Number(a.score.functionKept) ||
      a.score.unresolvedAltered - b.score.unresolvedAltered ||
      a.rung - b.rung ||
      a.score.voiceLeading - b.score.voiceLeading,
  );
}

/** Suggestions for an authored chord by id (the chord editor's question). */
export function suggestForChord(
  score: Score,
  chordId: string,
  options: SuggestOptions = {},
): ReharmCandidate[] {
  const ctx = buildReharmContext(score);
  const at = sequenceIndexOf(ctx, chordId);
  return at < 0 ? [] : suggest(ctx, at, options);
}
